/**
 * The hero frame, art-directed between two separately composed images.
 *
 * A 16:9 hero cropped to a phone loses the composition, so there is a second
 * portrait frame. Switching between them with two `<img>` elements and
 * `display: none` is the obvious way to do it and the wrong one: a hidden
 * image is still fetched, so every visitor downloaded both and paid for the
 * one they could not see (85kB instead of 36kB on a desktop, measured).
 *
 * `<picture>` exists precisely for this. The `media` attribute on a `<source>`
 * is evaluated before anything is requested, so exactly one file is fetched -
 * and the breakpoint here is the same 700px the stylesheet uses to swap the
 * hero's scrim, because the two decisions have to agree.
 */

const PORTRAIT_BELOW = "(max-width: 700px)";

function srcset(base: string, widths: number[], extension: string): string {
  return widths.map((width) => `${base}-${width}.${extension} ${width}w`).join(", ");
}

export function HeroArt() {
  return (
    <picture>
      <source
        media={PORTRAIT_BELOW}
        type="image/avif"
        srcSet={srcset("/landing/hero-portrait", [900, 600], "avif")}
        sizes="100vw"
      />
      <source
        media={PORTRAIT_BELOW}
        type="image/webp"
        srcSet={srcset("/landing/hero-portrait", [900, 600], "webp")}
        sizes="100vw"
      />
      <source
        type="image/avif"
        srcSet={srcset("/landing/hero-wide", [1920, 1280, 800], "avif")}
        sizes="100vw"
      />
      <source
        type="image/webp"
        srcSet={srcset("/landing/hero-wide", [1920, 1280, 800], "webp")}
        sizes="100vw"
      />
      {/*
        The hero is the largest contentful paint, so it is the one image that
        loads eagerly and at high priority. `alt=""` because it is decorative:
        the headline beside it already says what the page is, and describing
        "abstract light" to a screen reader adds noise, not meaning.
      */}
      <img
        {...{ fetchpriority: "high" }}
        src="/landing/hero-wide-1920.jpg"
        width={1920}
        height={1080}
        alt=""
        className="lhero__img"
        loading="eager"
        decoding="sync"
      />
    </picture>
  );
}
